import { ActionButton, DirectionalHint, IStyle, MessageBar, MessageBarType, ProgressIndicator, Stack, TooltipHost } from '@fluentui/react';
import {
    DocumentCard,
    DocumentCardActivity, DocumentCardPreview, DocumentCardTitle, IDocumentCardProps
} from '@fluentui/react/lib/DocumentCard';
import moment from 'moment';
import * as React from 'react';
import { useRef } from 'react';
import { b64toBlob, getFileName, getMegabytesToBytes, getPreviewProps, officeFileExtensions, onOpenFileInTab, saveBase64, sendApiRequest, thisIsDevEnvironment } from '../lib/helper';
import { IAccreditationRulesContext } from '../providers/accreditationrulesprovider';
import { useAssignmentContext } from '../providers/assignmentsprovider';
import { HelperService } from '../services/helperService';
import { IAssignment, ICourseWorkFlat } from '../types/IAssignment';
import { IAuthenticationStatusContext } from '../types/IAuthenticationStatusContext';
import { ISemester } from '../types/ICourse';
import { IContentData } from '../types/Shared';
import { previewCardHeight, previewCardWidth } from './galleryview';
import CourseTagsDisplay from './shared/courseTagsDisplay';
import { EmbedPDF } from './shared/PDFEmbed/EmbedPDF';

const PreviewCard: React.FunctionComponent<{
    index: number,
    file: ICourseWorkFlat,
    rootStyle?: IStyle,
    setSlideShowItem?: (file: ICourseWorkFlat) => void,
    authContext?: IAuthenticationStatusContext,
    accreditationRulesContext?: IAccreditationRulesContext,
    shouldGetDataFromCanvasAPIs: boolean,
    semester: ISemester;
    maxFileSizeBeforeStream: number;
}> = ({ index, file, rootStyle, setSlideShowItem, authContext, accreditationRulesContext, shouldGetDataFromCanvasAPIs, semester, maxFileSizeBeforeStream }) => {

    const [base64, setBase64] = React.useState<IContentData>();
    const [isDownloadingLargeFile, setIsDownloadingLargeFile] = React.useState<number | undefined>(undefined)
    const [loading, setLoading] = React.useState<boolean>(true);
    const [fileHelperServer, setFileHelperServer] = React.useState<string>();
    const assignmentContext = useAssignmentContext();
    const abortController = useRef(new AbortController());
    const currentAssignment = useRef<IAssignment | undefined>(assignmentContext?.SelectedAssignment)

    React.useEffect(() => {
        HelperService.getKeplerEnvironmentVariable("REACT_APP_KEPLER_FILE_HELPER_SERVER").then((res: string | undefined) => {
            setFileHelperServer(res);
        });    
    }, []);

    React.useEffect(() => {
        if (currentAssignment.current?.Path !== assignmentContext.SelectedAssignment?.Path) {
            abortController.current.abort();
        }
        currentAssignment.current = assignmentContext.SelectedAssignment;
    }, [assignmentContext.SelectedAssignment])

    React.useEffect(() => {
        if (fileHelperServer) {
            if (officeFileExtensions.includes(file.FileExtension.toLowerCase())) {
                if (fileHelperServer && fileHelperServer.length > 0) {
                    setBase64({ base64: file.FilePath, isThumbnail: false });
                }
            }
            else {
                const url = `/api/blobStorage/getBlobAsBase64FromPathForPreview?path=${encodeURIComponent(file.FilePath)}`;
                sendApiRequest(url, "GET", undefined, abortController.current.signal).then(res => {
                    setBase64(res);
                    setLoading(false);
                }).finally(() => {
                    setLoading(false);
                });
            }
        }
        return () => {
            setBase64(undefined);
        }
    }, [file, fileHelperServer]);

    const fileName = getFileName(authContext, accreditationRulesContext?.Result, file, index);

    let el = <Stack>
        <DocumentCardPreview {...getPreviewProps(fileName, undefined, previewCardHeight, loading)} />
        {loading && <ProgressIndicator styles={{ itemProgress: { padding: 0 } }} />}
    </Stack>;

    if (base64) {
        switch (file.FileExtension.toLowerCase()) {
            case ".png":
            case ".jpg":
            case ".jpeg":
            case ".gif":
            case ".svg":
                el = <DocumentCardPreview {...getPreviewProps(fileName, base64, previewCardHeight, loading)} />;
                break;
            case ".pdf":
                el = <EmbedPDF fileBlob={b64toBlob(base64.base64)} fileName={`${file.Student} - ${file.FileName}`} height={`${previewCardHeight.toString()}px`} width={`${previewCardWidth.toString()}px`} showToolbar={false} />
                break;
            case ".mp4":
            case ".mov":
                el = <video style={{ height: previewCardHeight, maxWidth: previewCardWidth }} poster={`data:image/png;base64,${base64.base64}`} controls />
                break;
            case ".docx":
            case ".docm":
            case ".dotm":
            case ".dotx":
            case ".doc":
            case ".pptx":
            case ".ppsx":
            case ".ppt":
            case ".pps":
            case ".pptm":
            case ".potm":
            case ".ppam":
            case ".potx":
            case ".ppsm":
            case ".xlsx":
            case ".xlsb":
            case ".xls":
            case ".xlsm":
                const keplerOfficeServerUrl = fileHelperServer;
                const fileUrl = `${keplerOfficeServerUrl}${file.FilePath}`;
                const filePath = thisIsDevEnvironment ? "newteach.pbworks.com%2Ff%2Fele%2Bnewsletter.docx" : encodeURIComponent(fileUrl);
                el = <iframe title={file.FileName} style={{ height: previewCardHeight, width: previewCardWidth }} src={`https://view.officeapps.live.com/op/embed.aspx?src=${filePath}`} />
                break;
        }
    }
    let docCardProps: IDocumentCardProps = {};
    if (setSlideShowItem) {
        docCardProps.onClick = () => {
            const maxFileSizeBytes = getMegabytesToBytes(maxFileSizeBeforeStream);
            if ((file.BlobProperties.contentLength || 0) > maxFileSizeBytes) {
                setIsDownloadingLargeFile(maxFileSizeBeforeStream);
                _downloadFile(false);
            }
            else {
                setSlideShowItem(file)
            }
        }
    }

    const _downloadFile = (isAttachment: boolean) => {
        const apiPath = `/api/blobStorage/getFileStream/${file.FileName}?path=${encodeURIComponent(file.FilePath)}${isAttachment ? "&attachment=true" : ""}`;
        const url = thisIsDevEnvironment ? `https://localhost:8080${apiPath}` : apiPath;
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        if(!isAttachment)
        {
            a.target = "_blank";
        }
        a.click();
    }

    return <DocumentCard
        {...docCardProps}
        style={!file.MatchesCurrentSearch ? { display: "none" } : undefined}
        styles={{ root: rootStyle || { width: 320, height: 346 } }}
        key={file.FilePath}
        aria-label={fileName}
    >
        <TooltipHost tooltipProps={{ directionalHint: DirectionalHint.rightCenter }} content={(file.Tags || []).length > 0 ? <Stack><h3>Tags</h3><CourseTagsDisplay tags={file.Tags || []} /></Stack> : undefined}>
            {el}
            <DocumentCardTitle
                styles={{ root: { float: "none", height: "inherit" } }}
                title={
                    fileName
                }
            />
            {base64?.base64 && <Stack horizontal>
                <ActionButton iconProps={{ iconName: "Download" }} onClick={(ev) => {
                    ev.preventDefault();
                    ev.stopPropagation();
                    if (!base64.isThumbnail) {
                        saveBase64(base64.base64, fileName)
                    }
                    else {
                        //_downloadFile(true);
                        const apiPath = `/api/blobStorage/getFileStream/${file.FileName}?path=${encodeURIComponent(file.FilePath)}&attachment=true"`;
                        const url = thisIsDevEnvironment ? `https://localhost:8080${apiPath}` : apiPath;
                        onOpenFileInTab(ev as React.MouseEvent<HTMLElement>, url, fileName);
                    }
                }}>Download</ActionButton>
            </Stack>}
            <DocumentCardActivity styles={{}} activity={shouldGetDataFromCanvasAPIs ? `Modified ${moment(file.BlobProperties.lastModified).format("M/D/YYYY h:mm A")}` : `${semester.Semester} ${semester.Year}`} people={[{ name: file.Student, profileImageSrc: "" }]} />
            {isDownloadingLargeFile && <MessageBar onDismiss={(ev) => {
                ev?.stopPropagation();
                setIsDownloadingLargeFile(undefined)
            }} messageBarType={MessageBarType.info}>The file is larger than {isDownloadingLargeFile} MB, so it will be downloaded instead</MessageBar>}
        </TooltipHost>
    </DocumentCard >;
};

export default PreviewCard;